import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const Gibsons = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const BcNeighbourhoodsLink =
    'https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/'

  const cheapNeighbourhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e7%a7%9f%e6%88%bf%e5%ae%9d%e5%85%b8-%e5%a4%a7%e6%b8%a9%e5%93%a5%e5%8d%8e%e7%a7%9f%e9%87%91%e6%9c%80%e4%be%bf%e5%ae%9c%e7%9a%8410%e4%b8%aa%e5%9c%b0%e5%8c%ba/'
    : 'https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/'

  const getAbout = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.gibsons:About Gibsons Paragraph 1">
            Gibsons, a coastal gem on the southern shores of{' '}
            <LinkText href="/rental-listings/british-columbia">
              British Columbia (BC)
            </LinkText>
            , Canada, graces the Sunshine Coast with its natural beauty and
            vibrant arts scene. Nestled along the Strait of Georgia, it boasts
            stunning landscapes of rugged coastlines, lush forests, and
            panoramic ocean vistas, making it a favored filming location.
            Outdoor enthusiasts relish the wealth of activities, from hiking and
            kayaking to beachcombing. Gibsons nurtures a thriving arts and
            cultural community, with galleries, live performances, and a rich
            history.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.gibsons:About Gibsons Paragraph 2">
            Beyond tourism, the local economy encompasses agriculture, forestry,
            and a burgeoning arts sector. The town’s close-knit and friendly
            community thrives amid its small-town charm. With BC Ferries
            providing easy mainland access, Gibsons combines coastal living with
            proximity to{' '}
            <LinkText href={BcNeighbourhoodsLink}>
              cities and neighbourhoods in BC
            </LinkText>
            , making it a desirable destination for residents and visitors
            alike.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={cheapNeighbourhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$1,200 - $2,200',
          bed2: '$1,500 - $2,500',
          bed3: '$2,000 - $3,500'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.gibsons:Apartment Hunting in Gibsons Paragraph">
            Compared to cities like{' '}
            <LinkText href="/rental-listings/city/vancouver">
              Vancouver
            </LinkText>{' '}
            and{' '}
            <LinkText href="/rental-listings/city/richmond">Richmond</LinkText>,
            it’s easier to find{' '}
            <LinkText href={links.under1500}>cheap rentals in Gibsons</LinkText>{' '}
            – although{' '}
            <LinkText href={links.under1000}>
              apartments for rent under $1000
            </LinkText>{' '}
            are still uncommon.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.gibsons:Apartments for rent Gibsons Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in Gibsons
              </LinkText>{' '}
              are popular among singles, couples, and small families. These
              units, ranging from older, character-filled buildings to newer,
              modern complexes, are often conveniently located close to local
              amenities like shops, cafes, and the waterfront. Apartments in
              Gibsons provide a practical and low-maintenance living option,
              ideal for those who enjoy the relaxed coastal lifestyle.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.gibsons:Houses for rent Gibsons Paragraph">
              <LinkText href={links.house}>Houses for rent in Gibsons</LinkText>{' '}
              appeal to families or individuals seeking more space and privacy.
              Many of these homes come with yards or gardens, offering a serene
              setting ideal for outdoor living and enjoying the region’s natural
              beauty. The houses in Gibsons vary from charming, older properties
              with unique features to newer, contemporary designs, catering to a
              range of preferences.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.gibsons:Townhouses for rent Gibsons Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Gibsons
              </LinkText>{' '}
              offer a blend of community living and private space. These are
              particularly popular among small families, couples, or
              professionals. Townhouses in Gibsons often provide multiple levels
              of living space and may come with small private yards or patios,
              and they are usually part of smaller developments, offering a
              sense of community.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.gibsons:Basements for rent Gibsons Paragraph">
              Basements for rent in Gibsons provide an affordable housing
              option, especially appealing to singles, young professionals, or
              those on a tighter budget. These units, typically part of a larger
              house, offer basic amenities and privacy at a lower cost, though
              they may have limitations in terms of natural light and size.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.gibsons:Condos for rent Gibsons Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Gibsons
              </LinkText>{' '}
              cater to those looking for a maintenance-free lifestyle with a
              sense of community. These units, which can range from quaint and
              cozy to more upscale and modern, are popular among young
              professionals, retirees, or small families. Condos in Gibsons
              often offer amenities like communal gardens or shared social
              spaces.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.gibsons:Rooms for rent Gibsons Paragraph">
              <LinkText href={links.room}>Renting a room in Gibsons</LinkText>{' '}
              is the most economical choice, often preferred by students,
              seasonal workers, or individuals seeking flexible living
              arrangements. Rooms for rent in Gibsons typically involve sharing
              common facilities such as kitchens and living rooms with other
              occupants, offering a social and communal living experience.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.gibsons:Short term rentals Gibsons Paragraph">
              Gibsons, a small coastal town on the Sunshine Coast of British
              Columbia, offers a variety of employment opportunities,
              particularly in the tourism, retail, and arts sectors. While it
              may not be renowned for a wide range of academic institutions, its
              unique coastal charm and close-knit community appeal to those
              seeking a quieter lifestyle.{' '}
              <LinkText href={links.shortTerm}>
                Short-term rentals in Gibsons
              </LinkText>{' '}
              are popular, especially during the summer months when tourist
              activity peaks, rather than being consistently high throughout the
              year as seen in larger cities or towns with significant student
              populations. The{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                flexibility of short-term rentals is a significant aspect for
                many renters
              </LinkText>
              , especially those looking for temporary accommodations that align
              with seasonal work, leisure, or simply a desire to experience
              living in this scenic area.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.gibsons:Cheap rentals Gibsons Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in Gibsons
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that in Gibsons, there are neighbourhoods where
              rent is considerably cheaper, so exploring these options is a good
              idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.gibsons:Living in Gibsons Paragraph">
            Nestled in the heart of British Columbia, Gibsons offers a
            delightful blend of natural beauty and small-town charm. Located
            along the scenic Sunshine Coast, this quaint coastal community
            boasts breathtaking views of the surrounding mountains, serene
            forests, and pristine waters. Known for its welcoming atmosphere and
            tight-knit community, Gibsons provides a wide range of amenities for
            residents and visitors alike. Outdoor enthusiasts can partake in
            activities such as hiking, boating, and kayaking, while those
            seeking culture can explore the vibrant local arts and history
            scene. With its tranquil ambiance and laid-back lifestyle, Gibsons
            is the perfect destination for those seeking a peaceful coastal
            getaway.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.gibsons:Living in Gibsons Paragraph 2">
            If you’re not sure that Gibsons is the right city for you, check out
            some of these resources comparing the different cities.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e5%8a%a0%e6%8b%bf%e5%a4%a7%e7%95%99%e5%ad%a65%e5%a4%a7%e5%9f%8e%e5%b8%82-%e7%a7%9f%e6%88%bf%e3%80%81%e5%ad%a6%e8%b4%b9%e3%80%81%e5%b7%a5%e4%bd%9c%e3%80%81%e7%ad%be%e8%af%81%e8%af%a6%e8%a7%a3/">
              加拿大最佳移民城市
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e6%b8%a9%e5%93%a5%e5%8d%8e%e3%80%81%e5%a4%9a%e4%bc%a6%e5%a4%9a%e6%af%94%e8%be%83/">
              温哥华 vs 多伦多：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/%E6%B8%A9%E5%93%A5%E5%8D%8E%E5%92%8C%E7%B4%A0%E9%87%8C%E5%B1%85%E4%BD%8F%E6%88%90%E6%9C%AC%E6%AF%94%E8%BE%83/">
              温哥华 vs 素里：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/toronto-zh/2021%e7%89%88%e5%a4%9a%e4%bc%a6%e5%a4%9a%e3%80%81%e8%92%99%e7%89%b9%e5%88%a9%e5%b0%94%e7%94%9f%e6%b4%bb%e6%88%90%e6%9c%ac%e6%af%94%e8%be%83/">
              多伦多 vs 蒙特利尔：生活成本比较
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/2020/11/best-canadian-cities-for-immigration/">
              Best Canadian cities for immigration
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2019/11/vancouver-or-toronto-a-cost-of-living-comparison/">
              Vancouver vs Toronto: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/02/surrey-vs-vancouver-bc-comparison/">
              Vancouver vs Surrey: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/04/toronto-vs-montreal-cost-of-living-comparison/">
              Toronto vs Montreal: Cost of living comparison
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.gibsons:Popular Neighbourhoods Gibsons Paragraph">
          Like any city, Gibsons is made up of smaller neighbourhoods with their
          own distinct features. Some of the most popular neighbourhoods include
          Gibsons Landing, Lower Gibsons, Upper Gibsons, Cedar Grove, and
          Langdale. For more info about Gibsons, you can consult this guide on{' '}
          <LinkText href={BcNeighbourhoodsLink}>BC neighbourhoods</LinkText>.
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.gibsons:Local Landmarks Gibsons Paragraph">
          Gibsons, BC, is a town renowned for its captivating landmarks that
          highlight the area’s beauty and cultural importance. One of the
          notable landmarks in Gibsons is the Gibsons Public Market, a vibrant
          hub where locals and visitors can experience the region’s culinary
          delights and artisanal crafts. Another prominent landmark is the
          iconic Molly’s Reach, a waterfront restaurant made famous by the
          television show &quot;The Beachcombers.&quot; For history enthusiasts,
          the Sunshine Coast Museum and Archives offers a fascinating journey
          into the town’s past, showcasing exhibits that depict its maritime
          heritage and indigenous culture. Lastly, the stunning Soames Hill Park
          boasts panoramic views, challenging hiking trails, and an opportunity
          to immerse oneself in the region’s breathtaking natural surroundings.
          These landmarks make Gibsons an enchanting destination for both
          residents and tourists seeking to explore its unique charm.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default Gibsons
